import React, { useState, useEffect } from "react";
import { ChevronRight, ExternalLink, Calendar, Loader2, Globe, Clock, Activity, FileText } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface NewsItem {
  id: string;
  title: string;
  link: string;
  description: string;
  imageUrl: string;
}

export default function DermatologyNewsViewer({ onBack }: { onBack: () => void }) {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState<"ar" | "en">("ar");

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const endpoint = lang === "ar" ? "/api/medical-news?limit=20" : "/api/medical-news-en?limit=20";
        const res = await fetch(endpoint);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            setNews(data);
          } else {
            setNews([]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch news:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [lang]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pt-16 md:pt-24 pb-8 md:pb-12 px-4 relative overflow-hidden" dir="rtl">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-[40vh] md:h-80 bg-slate-900 rounded-b-[4rem] z-0" />
      
      <div className="max-w-7xl mx-auto w-full relative z-10 flex-1 flex flex-col">
        <button 
          onClick={onBack}
          className="self-end mb-8 bg-white/10 hover:bg-white/20 text-white rounded-full py-2 px-5 flex items-center gap-2 transition-all border border-white/20 backdrop-blur-md"
        >
          <ChevronRight className="w-4 h-4" />
          <span className="text-sm font-bold">{lang === "ar" ? "العودة للبوابة" : "Back to Portal"}</span>
        </button>

        <div className="flex justify-center mb-6">
          <div className="bg-white/10 p-1 rounded-full border border-white/20 backdrop-blur-md flex items-center gap-1">
            <button
              onClick={() => setLang("ar")}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all ${lang === "ar" ? "bg-indigo-500 text-white shadow-lg" : "text-white/70 hover:text-white"}`}
            >
              عربي
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all ${lang === "en" ? "bg-indigo-500 text-white shadow-lg" : "text-white/70 hover:text-white"}`}
            >
              English
            </button>
          </div>
        </div>

        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-indigo-500/20 rounded-full mb-4">
            <Globe className="w-10 h-10 text-indigo-400" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">
            {lang === "ar" ? "أحدث الأخبار الطبية والبحوث" : "Latest Medical News & Research"}
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
            {lang === "ar" ? "تغطية حية لأحدث الأبحاث السريرية، والعلاجات المبتكرة، والأخبار في مجال طب الأمراض الجلدية حول العالم." : "Live coverage of the latest clinical research, innovative treatments, and dermatology news worldwide."}
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
            <p className="text-slate-500 font-medium">جاري جلب أحدث الأخبار من المصادر الطبية...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            <AnimatePresence>
              {news.map((item, idx) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-3xl overflow-hidden shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 border border-slate-100 flex flex-col group cursor-pointer"
                  onClick={() => window.open(item.link, '_blank')}
                >
                  <div className="relative h-56 w-full overflow-hidden bg-slate-100">
                    <img 
                      src={item.imageUrl} 
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                      <Activity className="w-3.5 h-3.5 text-indigo-600" />
                      <span className="text-xs font-bold text-slate-800">تحديث طبي</span>
                    </div>
                  </div>
                  
                  <div className="p-4 md:p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-4 text-xs font-semibold text-slate-400 mb-4">
                      <div className="flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" />
                        <span>MedicalXpress</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        <span>منذ قليل</span>
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-slate-800 mb-3 leading-snug group-hover:text-indigo-600 transition-colors line-clamp-3" dir="ltr">
                      {item.title}
                    </h3>
                    
                    <p className="text-sm text-slate-500 leading-relaxed mb-6 line-clamp-3 flex-1" dir="ltr">
                      {item.description.replace(/<[^>]+>/g, '')}
                    </p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-auto">
                      <span className="text-indigo-600 font-bold text-sm">قراءة المقال كاملاً</span>
                      <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                        <ExternalLink className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {news.length === 0 && !loading && (
              <div className="col-span-full py-20 text-center">
                <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2">لا توجد أخبار متاحة حالياً</h3>
                <p className="text-slate-500">يرجى المحاولة مرة أخرى لاحقاً.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
